import { useEffect, useMemo, useState } from "react";
import {
  applyBulkFacilityScope,
  applyTemplateToDraft,
  buildPermissionDraft,
  enabledPermissionCount,
  scopeOptions,
  serializePermissionDraft,
} from "./permissionManagement";

const ROLE_LABELS = { president:"社長",manager:"幹部",staff:"社員",part:"パート" };

async function permissionRequest(path = "", method = "GET", body = null) {
  const options = { method,credentials:"include",headers:{ "Content-Type":"application/json" } };
  if (body) options.body = JSON.stringify(body);
  const response = await fetch(`/api/permission_management_api.php${path}`,options);
  const data = await response.json().catch(()=>({}));
  if (!response.ok) {
    const error = new Error(data.error || "権限設定の通信に失敗しました");
    error.status = response.status;
    error.reasonCode = data.reason_code;
    throw error;
  }
  return data;
}

const card = { background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16 };
const selectStyle = { width:"100%",padding:"9px 10px",border:"1px solid #d1d5db",borderRadius:8,background:"#fff",fontFamily:"inherit" };
const buttonStyle = (color,disabled=false) => ({ border:"none",borderRadius:8,padding:"9px 14px",background:disabled?"#d1d5db":color,color:disabled?"#777":"#fff",fontWeight:700,cursor:disabled?"not-allowed":"pointer",fontFamily:"inherit" });

export default function PermissionManagementPanel() {
  const [data,setData] = useState(null);
  const [selectedUserId,setSelectedUserId] = useState("");
  const [current,setCurrent] = useState(null);
  const [draft,setDraft] = useState({});
  const [templateKey,setTemplateKey] = useState("");
  const [bulkScope,setBulkScope] = useState("selected_stores");
  const [bulkStores,setBulkStores] = useState([]);
  const [expanded,setExpanded] = useState({});
  const [reason,setReason] = useState("");
  const [preview,setPreview] = useState(null);
  const [loading,setLoading] = useState(true);
  const [saving,setSaving] = useState(false);
  const [message,setMessage] = useState(null);

  const loadBase = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const result = await permissionRequest();
      setData(result);
      if (!selectedUserId && result.users?.length) setSelectedUserId(result.users[0].id);
    } catch(error) {
      setMessage({ type:"error",text:error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{ loadBase(); },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async userId => {
    if (!userId) return;
    setLoading(true);
    setMessage(null);
    setPreview(null);
    try {
      const result = await permissionRequest(`?user_id=${encodeURIComponent(userId)}`);
      setData(result);
      setCurrent(result.current);
      setDraft(buildPermissionDraft(result.permissions,result.current.grants));
      setTemplateKey("");
      setReason("");
      setExpanded(Object.fromEntries(result.categories.map(category=>[category.category_key,true])));
    } catch(error) {
      setCurrent(null);
      setMessage({ type:"error",text:error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{ if (data && selectedUserId) loadUser(selectedUserId); },[selectedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedUser = data?.users?.find(user=>user.id===selectedUserId);
  const permissionsByCategory = useMemo(()=>{
    const grouped = {};
    for (const permission of data?.permissions || []) {
      (grouped[permission.category_key] ||= []).push(permission);
    }
    return grouped;
  },[data]);

  const changeDraft = (key,patch) => {
    setPreview(null);
    setDraft(previous=>({ ...previous,[key]:{ ...previous[key],...patch } }));
  };

  const selectTemplate = key => {
    const template = data.templates.find(item=>item.template_key===key);
    setTemplateKey(key);
    setDraft(applyTemplateToDraft(data.permissions,template));
    setPreview(null);
    setMessage({ type:"info",text:`${template.display_name}（${template.permission_count}権限）を保存前の案へ反映しました` });
  };

  const toggleBulkStore = storeId => setBulkStores(previous => previous.includes(storeId)
    ? previous.filter(id=>id!==storeId) : [...previous,storeId]);

  const applyBulk = () => {
    if (bulkScope==="selected_stores" && !bulkStores.length) {
      setMessage({ type:"error",text:"一括設定する対象施設を1件以上選択してください" });
      return;
    }
    setDraft(previous=>applyBulkFacilityScope(previous,data.permissions,bulkScope,bulkStores));
    setPreview(null);
    setMessage({ type:"info",text:"ONになっている施設対象権限へ一括反映しました" });
  };

  const payload = () => ({
    target_user_id:selectedUserId,
    template_key:templateKey || null,
    permissions:serializePermissionDraft(data.permissions,draft),
  });

  const createPreview = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await permissionRequest("","POST",{ action:"preview",...payload() });
      setPreview({ plan:result.plan,permissions:payload().permissions });
    } catch(error) {
      setMessage({ type:"error",text:error.message });
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!preview || !reason.trim()) {
      setMessage({ type:"error",text:"変更理由を入力し、保存内容を確認してください" });
      return;
    }
    setSaving(true);
    setMessage(null);
    const requestId = `permission-ui-${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
    try {
      const result = await permissionRequest("","POST",{
        action:"save",
        target_user_id:selectedUserId,
        template_key:templateKey || null,
        permissions:preview.permissions,
        expected_permission_revision:preview.plan.permission_revision,
        plan_fingerprint:preview.plan.plan_fingerprint,
        approved:true,
        reason:reason.trim(),
        request_id:requestId,
      });
      await loadUser(selectedUserId);
      setMessage({ type:"success",text:`保存しました（revision ${result.permission_revision}、変更 ${result.changed_count ?? 0}件）` });
    } catch(error) {
      setPreview(null);
      setMessage({ type:"error",text:error.status===409 ? `${error.message} 再読込して確認してください。` : error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <div style={{ padding:24,color:"#777" }}>権限設定を読み込み中…</div>;
  if (!data) return <div style={{ ...card,color:"#b42318" }}>{message?.text || "権限設定を取得できません"}<button onClick={loadBase} style={{ ...buttonStyle("#457B9D"),marginLeft:12 }}>再試行</button></div>;

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
      {message && <div role="status" style={{ padding:"10px 12px",borderRadius:8,background:message.type==="error"?"#fff1f0":message.type==="success"?"#eafaf1":"#eef6ff",color:message.type==="error"?"#b42318":message.type==="success"?"#1f6b45":"#245b88",border:"1px solid #dde5ed",fontSize:13 }}>{message.text}</div>}

      <div style={card}>
        <div style={{ fontWeight:800,marginBottom:6 }}>対象ユーザー</div>
        <div style={{ fontSize:12,color:"#777",marginBottom:10 }}>有効な正本アカウントだけを表示します</div>
        <select aria-label="権限設定対象ユーザー" value={selectedUserId} onChange={event=>setSelectedUserId(event.target.value)} style={selectStyle}>
          {data.users.map(user=><option key={user.id} value={user.id}>{user.name}｜{ROLE_LABELS[user.role] || user.role}｜{data.facilities.find(store=>store.id===user.store_id)?.name || user.store_id}</option>)}
        </select>
        {selectedUser && <div style={{ marginTop:10,fontSize:12,color:"#555" }}>現在revision: <b>{current?.user?.permission_revision ?? selectedUser.permission_revision}</b>　ON: <b>{enabledPermissionCount(draft)}</b> / {data.permissions.length}</div>}
      </div>

      <div style={card}>
        <div style={{ fontWeight:800,marginBottom:8 }}>テンプレートから開始</div>
        <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
          {data.templates.map(template=><button key={template.template_key} onClick={()=>selectTemplate(template.template_key)} disabled={saving} style={buttonStyle(templateKey===template.template_key?"#2D6A4F":"#64748b",saving)}>{template.display_name}（{template.permission_count}）</button>)}
        </div>
        <div style={{ fontSize:11,color:"#777",marginTop:8 }}>テンプレートは保存前の案へ反映されます。個別変更後、確認画面を経て保存します。</div>
      </div>

      <div style={card}>
        <div style={{ fontWeight:800,marginBottom:8 }}>施設scopeを一括設定</div>
        <div style={{ display:"grid",gridTemplateColumns:"minmax(160px,220px) 1fr auto",gap:10,alignItems:"center" }}>
          <select aria-label="施設scope一括設定" value={bulkScope} onChange={event=>setBulkScope(event.target.value)} style={selectStyle}>
            <option value="home_store">所属施設</option><option value="selected_stores">選択した施設</option><option value="all_stores">全施設</option>
          </select>
          <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
            {data.facilities.map(store=><label key={store.id} style={{ fontSize:13,color:bulkScope==="selected_stores"?"#333":"#aaa" }}><input type="checkbox" disabled={bulkScope!=="selected_stores"} checked={bulkStores.includes(store.id)} onChange={()=>toggleBulkStore(store.id)}/> {store.name}</label>)}
          </div>
          <button onClick={applyBulk} disabled={saving} style={buttonStyle("#457B9D",saving)}>ON権限へ反映</button>
        </div>
      </div>

      {(data.categories || []).map(category=>{
        const categoryPermissions = permissionsByCategory[category.category_key] || [];
        const onCount = categoryPermissions.filter(permission=>draft[permission.permission_key]?.allowed).length;
        return <section key={category.category_key} style={card}>
          <button onClick={()=>setExpanded(previous=>({ ...previous,[category.category_key]:!previous[category.category_key] }))} style={{ width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",textAlign:"left" }}>
            <span><b>{category.display_name}</b><span style={{ fontSize:12,color:"#777",marginLeft:8 }}>{onCount}/{categoryPermissions.length} ON</span></span><span>{expanded[category.category_key]?"−":"＋"}</span>
          </button>
          {expanded[category.category_key] && <div style={{ marginTop:12,display:"grid",gap:8 }}>
            {categoryPermissions.map(permission=>{
              const value = draft[permission.permission_key] || {};
              const protectedPermission = permission.protected;
              return <div key={permission.permission_key} style={{ border:"1px solid #e5e7eb",borderRadius:10,padding:12,background:value.allowed?"#f0faf5":"#fafafa" }}>
                <div style={{ display:"flex",gap:12,alignItems:"flex-start" }}>
                  <label style={{ display:"flex",gap:9,alignItems:"center",flex:"1 1 280px",cursor:protectedPermission?"not-allowed":"pointer" }}>
                    <input aria-label={`${permission.display_name} ON/OFF`} type="checkbox" disabled={protectedPermission || saving} checked={Boolean(value.allowed)} onChange={event=>changeDraft(permission.permission_key,{ allowed:event.target.checked })}/>
                    <span><b style={{ fontSize:13 }}>{permission.display_name}</b>{permission.is_critical && <span style={{ marginLeft:6,fontSize:10,color:"#b45309" }}>重要</span>}<div style={{ fontSize:11,color:"#777",marginTop:3 }}>{permission.description}</div><div style={{ fontSize:10,color:"#999",marginTop:2 }}>{permission.permission_key}</div></span>
                  </label>
                  {value.allowed && scopeOptions(permission.scope_mode).length>1 && <div style={{ flex:"0 1 240px" }}>
                    <select aria-label={`${permission.display_name} scope`} value={value.scope_type} onChange={event=>changeDraft(permission.permission_key,{ scope_type:event.target.value,store_ids:[] })} style={selectStyle}>
                      {scopeOptions(permission.scope_mode).map(option=><option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>}
                </div>
                {protectedPermission && <div style={{ fontSize:11,color:"#8a5b00",marginTop:6 }}>最高権限は事故防止のため、この画面から変更できません。</div>}
                {value.allowed && value.scope_type==="selected_stores" && <div style={{ display:"flex",gap:12,flexWrap:"wrap",marginTop:9,paddingLeft:26 }}>
                  {data.facilities.map(store=><label key={store.id} style={{ fontSize:12 }}><input type="checkbox" aria-label={`${permission.display_name} ${store.name}`} checked={(value.store_ids || []).includes(store.id)} onChange={()=>changeDraft(permission.permission_key,{ store_ids:(value.store_ids || []).includes(store.id)?value.store_ids.filter(id=>id!==store.id):[...(value.store_ids || []),store.id] })}/> {store.name}</label>)}
                </div>}
              </div>;
            })}
          </div>}
        </section>;
      })}

      <div style={card}>
        <label style={{ display:"block",fontSize:12,fontWeight:700,marginBottom:5 }}>変更理由（監査ログへ記録）</label>
        <input aria-label="権限変更理由" value={reason} maxLength={500} onChange={event=>setReason(event.target.value)} placeholder="例：施設担当範囲の承認内容を反映" style={selectStyle}/>
        {!preview ? <button onClick={createPreview} disabled={saving || !current} style={{ ...buttonStyle("#2D6A4F",saving || !current),width:"100%",marginTop:12 }}>{saving?"確認中…":"保存内容を確認"}</button> : <div style={{ marginTop:12,padding:12,borderRadius:10,background:"#fff8df",border:"1px solid #f2d27a" }}>
          <div style={{ fontWeight:800,fontSize:13 }}>保存前確認</div>
          <div style={{ fontSize:12,marginTop:5 }}>付与 {preview.plan.counts.grant}件／OFF {preview.plan.counts.revoke}件／scope等変更 {preview.plan.counts.scope_change}件／変更なし {preview.plan.counts.unchanged}件</div>
          <div style={{ fontSize:11,color:"#777",marginTop:4 }}>revision {preview.plan.permission_revision} → {preview.plan.proposed_permission_revision}</div>
          <div style={{ display:"flex",gap:8,marginTop:10 }}><button onClick={()=>setPreview(null)} disabled={saving} style={buttonStyle("#64748b",saving)}>戻って修正</button><button onClick={save} disabled={saving || !reason.trim()} style={{ ...buttonStyle("#2D6A4F",saving || !reason.trim()),flex:1 }}>{saving?"保存中…":"この内容で保存"}</button></div>
        </div>}
      </div>
    </div>
  );
}
